import { useState, type FormEvent } from 'react'

import { errorMessage, isApiError } from '@/api/client'
import {
  cleanGrounds,
  formatDate,
  pipelineRunBody,
  useStartPipelineRun,
  type RunDraft,
} from '@/api/pipeline'
import { jurisdictionLabel, type Jurisdiction, type MatterDetail, type PipelineRun } from '@/api/types'
import { useAuth } from '@/auth/AuthProvider'
import { Button, Card, CardHeader, Field, Input, Textarea, useToast } from '@/components/ui'
import { useRouter } from '@/routes'
import { recallRunDefaults, rememberRunDefaults } from '@/runDefaults'

import { CASE_ANCHORS } from './nextStep'

/**
 * Run this matter again — the control whose absence was the whole complaint.
 *
 * *"the flow is still stuck"*: a matter blocked on a missing deadline, an
 * attorney who then recorded and signed one, and no way from this screen to ask
 * the pipeline to look again. Correcting the facts and re-running are one
 * motion in an attorney's head, and the screen implemented only the first half
 * — so the fix reads as having done nothing.
 *
 * ## Why it asks for the document again
 *
 * `document_text` is required on every run and **nothing stores it**
 * (`PipelineRunIn.document_text`: *"nothing stores it, so every caller supplies
 * it fresh"*). That is the same reason the New case form and the assessment
 * pack re-ask for it: the client's office action is not kept on our side once
 * the run that read it has finished. A re-run therefore cannot be one button,
 * and pretending otherwise would mean storing the document to make a button
 * possible — the wrong trade, and the copy says so rather than making the
 * paste look like an oversight.
 *
 * Everything that *is* stored is carried over silently: the office, the grounds
 * and the client facts come off the run being viewed, so the only thing to
 * supply is the paste itself.
 *
 * ## The first run of an opposition
 *
 * An opposition is opened on the Opposition screen and answered from here, and
 * the run that answers it differs from a re-run in three ways. It is a
 * **counterstatement** (`doc_role`), not a response: sending the default would
 * draft an examination-style reply to a notice of opposition. There is **no
 * earlier run** to carry the grounds from, so the grounds the notice pleads are
 * asked for (prefilled from what the Opposition screen was told). And the
 * deadline rule counts from the **date on the notice**, which no run holds
 * either: without it the reconciler has nothing to count from and blocks the
 * run with `RULE_NOT_RUN_NO_REPORT_DATE`. Both fields stay visible and
 * editable, so a prefill that is stale costs a correction and never changes
 * what is sent behind the person's back.
 *
 * ## Why `generate` defaults on here and off on New case
 *
 * Deliberately different, and not an inconsistency. New case defaults to a dry
 * run because the first thing you do with an unseen document is find out
 * whether the guards like it, and that should be free. A re-run from this
 * screen is reached by an attorney who has just cleared what stopped the last
 * one, and whose next sentence is "so where is my draft" — defaulting to off
 * would hand them a second run with no draft and reproduce the stuck feeling
 * this control exists to end. The cost is stated on the toggle either way.
 */
export function RerunCard({
  matter,
  run,
  neverRun = false,
}: {
  matter: MatterDetail | undefined
  run: PipelineRun | null | undefined
  /**
   * The run list has loaded and is empty. Not `!run`: that is also true while
   * the selected run is still loading, when the card must not flicker into
   * first-run wording on a matter that has plenty of them.
   */
  neverRun?: boolean
}) {
  const { hasScope } = useAuth()
  const router = useRouter()
  const toast = useToast()
  const startRun = useStartPipelineRun()

  const [documentText, setDocumentText] = useState('')
  const [generate, setGenerate] = useState(true)
  // `null` means "untouched": the field shows what the matter already knows and
  // follows it as it loads, and only becomes the person's own once they type.
  const [reportDateEdit, setReportDateEdit] = useState<string | null>(null)
  const [groundsEdit, setGroundsEdit] = useState<string | null>(null)
  const [runError, setRunError] = useState<string | null>(null)

  const canWrite = hasScope('matters:write')
  const terminal = matter?.lifecycle === 'CLOSED' || matter?.lifecycle === 'DECLINED'

  if (!matter) return null

  const isOpposition = matter.proceeding_type === 'OPPOSITION'
  const recalled = recallRunDefaults(matter.id)
  // A matter that has never run has nothing to carry over, so the two things a
  // run cannot do without are asked for here. An opposition asks every time:
  // `PipelineRun` does not hold the notice date, so it could not be carried.
  const showDate = isOpposition || neverRun
  const showGrounds = isOpposition || neverRun || run?.grounds.length === 0
  const defaultGrounds = run?.grounds.length ? run.grounds : (recalled?.grounds ?? [])
  const reportDate = reportDateEdit ?? recalled?.reportDate ?? ''
  const groundsText = groundsEdit ?? defaultGrounds.join('\n')
  const inIntake = matter.lifecycle === 'INTAKE'

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (!matter || !documentText.trim()) return
    setRunError(null)

    // Carried from the run being viewed rather than re-asked: these are facts
    // the matter already holds, and making somebody retype grounds to re-run is
    // how a re-run stops being worth doing.
    const draft: RunDraft = {
      documentText,
      grounds: showGrounds ? cleanGrounds(groundsText.split(/[\n,]/)) : (run?.grounds ?? []),
      office: (run?.office ?? matter.jurisdiction) as Jurisdiction | null,
      kind: run?.kind ?? null,
      // An opposition is answered by a counterstatement; the server refuses the
      // reverse (a counterstatement on an examination matter) with a 409.
      docRole: isOpposition ? 'counterstatement' : 'response',
      reportDate: showDate ? reportDate : '',
      // The confirmed deadline, carried in as the date off the papers — and the
      // single thing that makes this control work rather than merely look like
      // it works.
      //
      // `reconcile()` is pure over (rule, report date, printed date). It never
      // reads `matters.deadline_date`, so a re-run that sent nothing here would
      // hand it the same two empty inputs as last time and come back with the
      // identical refusal: the attorney records a date, signs it, re-runs, and
      // watches the matter block again for want of the date they just entered.
      // With it supplied, a matter whose office has no rule moves from
      // NO_INPUT/`DEADLINE_UNKNOWN` to NO_RULE/`DEADLINE_UNRECONCILED`, which
      // the reconciler does not block on.
      //
      // Honest, not a fudge: `printed_deadline` is the date on the letter, the
      // runner records whether it was supplied or extracted, and the deadline
      // field this comes from asks for exactly that — "from the papers, or from
      // the office if the papers do not print one".
      printedDeadline: matter.deadline_date ?? '',
      caseFacts: run?.case_facts ?? '',
      generate,
      // The server upgrades the default to whatever this matter's kind actually
      // needs (`resolve_variant`), so naming one here would only override a
      // better choice.
      variant: 'draft_v5',
      maxRetries: 1,
    }

    try {
      const started = await startRun.mutateAsync({
        matterId: matter.id,
        body: pipelineRunBody(draft),
      })
      setDocumentText('')
      // Kept for the next run in this sitting: a run does not record the date on
      // the notice, so without this an opposition would ask for it again on
      // every re-run.
      rememberRunDefaults(matter.id, {
        reportDate: showDate ? reportDate : (recalled?.reportDate ?? ''),
        grounds: draft.grounds,
      })
      toast.push(generate ? 'Running — a draft was requested' : 'Running as a dry run', {
        tone: 'success',
        description: generate
          ? 'The checks run first; the model is only called if they pass.'
          : 'Every check runs and no model is called, so this costs nothing.',
      })
      router.navigate(`/case/${matter.id}/${started.id}`)
    } catch (error) {
      // Both: the toast is pinned for errors, and the message also stays here
      // beside the button, where the person who pressed it is looking. A 409
      // carries the server's reason in full (not confirmed, wrong document,
      // terminal), and a refusal nobody can read is indistinguishable from a
      // button that did nothing.
      const message =
        isApiError(error) && error.status === 409 ? error.message : errorMessage(error)
      setRunError(message)
      toast.push('Could not start the run', { tone: 'error', description: message })
    }
  }

  const office = run?.office ?? matter.jurisdiction
  const carried = [
    office ? jurisdictionLabel(office as Jurisdiction) : null,
    run?.grounds.length ? `${run.grounds.join(', ')}` : null,
    matter.deadline_date ? `the ${formatDate(matter.deadline_date)} deadline` : null,
    run?.case_facts?.trim() ? 'your client facts' : null,
  ].filter(Boolean)
  const noun = isOpposition ? 'notice of opposition' : 'office action'

  return (
    <Card
      id={CASE_ANCHORS.rerun}
      tabIndex={-1}
      role="group"
      aria-label={neverRun ? 'Run this matter' : 'Run this matter again'}
      className="gap-3"
    >
      <CardHeader
        kicker={neverRun ? 'Run' : 'Run again'}
        title={neverRun ? 'Run this matter' : 'Run this matter again'}
        description={
          !neverRun
            ? 'Once you have fixed whatever stopped the last run, ask the pipeline to look again. Each run is kept separately — nothing is overwritten.'
            : isOpposition
              ? 'Paste the notice of opposition to draft the counterstatement. Each run is kept separately — nothing is overwritten.'
              : 'Paste the document to run the checks and, if you want it, draft the response. Each run is kept separately — nothing is overwritten.'
        }
      />

      {terminal ? (
        <p className="m-0 text-body text-muted">
          This matter is {matter.lifecycle.toLowerCase()}, so nothing further runs on it.
          Closing a matter is deliberate and final.
        </p>
      ) : !canWrite ? (
        <p className="m-0 text-body text-muted">
          Starting a run needs the matters:write permission, which this login does not have.
        </p>
      ) : (
        <form className="flex flex-col gap-3" onSubmit={onSubmit}>
          <Field
            label={`Paste the ${noun}${neverRun ? '' : ' again'}`}
            // The warning is on the box where the mistake is made, not in a
            // toggle further down. A run that finds the text too short to read,
            // or not in English, does not merely fail — it closes the matter
            // permanently, and it does that on a free run exactly as readily as
            // on a paid one (`decline_matter` is reached in s1 and s2, before
            // anything looks at whether a draft was asked for).
            hint="Paste the whole document, not an extract. We do not keep your client's document after a run finishes, so it has to be pasted fresh each time — and a run that finds it too short to read, or not in English, closes the matter for good."
          >
            {({ id, describedBy }) => (
              <Textarea
                id={id}
                aria-describedby={describedBy}
                rows={6}
                value={documentText}
                onChange={(event) => setDocumentText(event.target.value)}
                placeholder={
                  isOpposition
                    ? 'Paste the full text of the notice of opposition…'
                    : 'Paste the full text of the examination report or office action…'
                }
              />
            )}
          </Field>

          {showDate ? (
            <Field
              label={isOpposition ? 'Date on the notice' : 'Date on the report'}
              hint="The date the office issued it. The deadline rule counts from here: without it the run cannot work out a deadline and stops."
            >
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  type="date"
                  aria-describedby={describedBy}
                  value={reportDate}
                  onChange={(event) => setReportDateEdit(event.target.value)}
                />
              )}
            </Field>
          ) : null}

          {showGrounds ? (
            <Field
              label={isOpposition ? 'Grounds pleaded' : 'Grounds raised'}
              hint={
                isOpposition
                  ? 'The grounds the notice raises, one per line or comma-separated, for example 11(1). The counterstatement answers these.'
                  : 'One per line or comma-separated, for example 11(1). Left empty, the run reads them off the document.'
              }
            >
              {({ id, describedBy }) => (
                <Textarea
                  id={id}
                  aria-describedby={describedBy}
                  rows={2}
                  value={groundsText}
                  onChange={(event) => setGroundsEdit(event.target.value)}
                />
              )}
            </Field>
          ) : null}

          {carried.length > 0 ? (
            <p className="m-0 text-micro text-muted">
              Carried over from the last run: {carried.join(' · ')}. You do not need to re-enter
              them.
            </p>
          ) : null}

          <label className="flex items-start gap-3 text-body">
            <input
              type="checkbox"
              className="mt-1"
              checked={generate}
              onChange={(event) => setGenerate(event.target.checked)}
            />
            <span>
              <span className="font-medium">Write the draft too</span>
              <span className="mt-0.5 block text-meta text-muted">
                On: about a minute, and one live model call. Off: the checks alone, in about a
                second, at no cost.
              </span>
            </span>
          </label>

          {inIntake ? (
            <p role="note" className="m-0 rounded-md border border-warn-line bg-warn-bg px-3 py-2 text-meta text-warn-fg">
              Nothing can run yet: nobody has confirmed this matter&rsquo;s facts. New case and
              Opposition confirm them when they open a matter; this one was opened another way
              and still needs confirming.
            </p>
          ) : null}

          {runError ? (
            <p role="alert" className="m-0 text-body text-block-fg">
              {runError}
            </p>
          ) : null}

          <div>
            <Button
              type="submit"
              variant="primary"
              disabled={startRun.isPending || !documentText.trim() || inIntake}
            >
              {startRun.isPending
                ? generate
                  ? 'Running — this can take a minute…'
                  : 'Running…'
                : generate
                  ? neverRun
                    ? 'Run and draft'
                    : 'Run again and draft'
                  : 'Run the checks only'}
            </Button>
          </div>
        </form>
      )}
    </Card>
  )
}
